import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import React, { useState } from "react";

import {
    RichNavigationCollectionContainer,
    RichNavigationLinkCollection as RichNavigationLinkCollectionValue,
} from "@reactivated";

import {
    classNames,
    concatClassNames,
} from "@thelabnyc/thelabui/src/utils/styles";

import { useRichNavViewport } from "../../hooks/useRichNavViewport";
import { isoWebPageURL } from "../../models/nominals";
import { linkCollectionVariantClass } from "../../models/rich-navigation";
import { Link } from "../Link";
import { RichNavigationLinkCollectionContent } from "../RichNavigationLinkCollection";

import styles from "./index.module.scss";

const linkCollectionCtaLabel = (
    collection: Pick<
        RichNavigationLinkCollectionValue,
        "cta_text" | "cta_link"
    >,
): string | null => collection.cta_text || collection.cta_link?.title || null;

// Not wrapped in Immutable<> (unlike leaf link components): `value` flows into
// thelabui's StreamField, whose `value: FieldValue` prop requires the exact
// mutable generated type, and castDraft isn't allowed outside reducers. This
// matches the sibling RichNavigationLinkCollection family.
type IBlockProps = {
    value: RichNavigationCollectionContainer;
    mobileContentIndex?: number;
    mobileA11yIdPrefix?: string;
};

const getCollections = (
    value: RichNavigationCollectionContainer,
): RichNavigationLinkCollectionValue[] => value.collections ?? [];

const ColumnHeader = (props: {
    title?: string | null;
    ctaUrl?: string;
    ctaLabel?: string | null;
}): React.JSX.Element | null => {
    return (
        <div className={styles.columnHeader}>
            <h3 className={styles.columnTitle}>{props.title}</h3>
            {props.ctaUrl && props.ctaLabel && (
                <Link
                    className={styles.subtitleLink}
                    href={isoWebPageURL.wrap(props.ctaUrl)}
                    target="_top"
                >
                    {props.ctaLabel}
                </Link>
            )}
        </div>
    );
};

const ColumnFooter = (props: {
    collection: RichNavigationLinkCollectionValue;
}): React.JSX.Element | null => {
    const footerLabel = props.collection.cta_text;
    const footerUrl = props.collection.cta_link?.url;
    if (!footerLabel || !footerUrl) {
        return null;
    }
    if (footerLabel === props.collection.cta_link?.title) {
        return null;
    }
    return (
        <Link
            className={styles.footerLink}
            href={isoWebPageURL.wrap(footerUrl)}
            target="_top"
        >
            {footerLabel}
        </Link>
    );
};

const MobileContentPanel = (props: {
    collections: RichNavigationLinkCollectionValue[];
    mobileIndex: number;
    mobileA11yIdPrefix?: string;
    headerCtaUrl?: string;
    headerCtaLabel?: string | null;
    showHeaderCta: boolean;
}): React.JSX.Element | null => {
    const { collections, mobileIndex, headerCtaUrl, headerCtaLabel } = props;
    const [openSubCategories, setOpenSubCategories] = useState<
        Record<number, boolean>
    >({});
    const subCategoryBaseId = React.useId();
    const toggleSubCategory = (idx: number) => {
        setOpenSubCategories((prev) => ({
            ...prev,
            [idx]: !prev[idx],
        }));
    };

    const firstCollection = collections[0];
    if (!firstCollection) {
        return null;
    }
    const subCollections = collections.slice(1);
    const firstCollectionVariant = linkCollectionVariantClass(
        firstCollection.variant,
    );

    return (
        <div className={styles.mobileColumns}>
            <div
                className={concatClassNames([
                    styles.mobileSection,
                    firstCollectionVariant,
                ])}
            >
                <div className={styles.mobileSectionHeader}>
                    <h3 className={styles.mobileSectionTitle}>
                        {firstCollection.name}
                    </h3>
                    {headerCtaUrl && headerCtaLabel && (
                        <Link
                            className={styles.mobileSectionSubtitle}
                            href={isoWebPageURL.wrap(headerCtaUrl)}
                            target="_top"
                        >
                            {headerCtaLabel}
                        </Link>
                    )}
                </div>
                <RichNavigationLinkCollectionContent
                    value={firstCollection}
                    isCurrentCollectionOpen={true}
                    mobileContentIndex={mobileIndex}
                    mobileA11yIdPrefix={props.mobileA11yIdPrefix}
                    suppressCta={props.showHeaderCta}
                />
                <ColumnFooter collection={firstCollection} />
            </div>

            {subCollections.map((collection, subIdx) => {
                const isSubOpen = openSubCategories[subIdx] ?? false;
                const subCollectionVariant = linkCollectionVariantClass(
                    collection.variant,
                );
                const subLabelId = `${subCategoryBaseId}-label-${subIdx}`;
                const subMenuId = `${subCategoryBaseId}-menu-${subIdx}`;
                return (
                    <div
                        key={`${collection.name}-${subIdx}`}
                        className={concatClassNames([
                            styles.mobileSubCategory,
                            isSubOpen ? styles.mobileSubCategoryOpen : "",
                            subCollectionVariant,
                        ])}
                    >
                        <button
                            type="button"
                            id={subLabelId}
                            className={styles.mobileSubCategoryTitle}
                            aria-expanded={isSubOpen}
                            aria-controls={subMenuId}
                            onClick={() => toggleSubCategory(subIdx)}
                        >
                            {collection.name}
                        </button>
                        {isSubOpen && (
                            <RichNavigationLinkCollectionContent
                                value={collection}
                                isCurrentCollectionOpen={true}
                                mobileContentIndex={mobileIndex}
                                labelId={subLabelId}
                                menuId={subMenuId}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export const RichNavigationCollectionContainerBlock = (
    props: IBlockProps,
): React.JSX.Element | null => {
    const viewport = useRichNavViewport();
    const collections = getCollections(props.value);
    if (collections.length === 0) {
        return null;
    }

    const triggerName =
        props.value.title?.trim() || collections[0]?.name?.trim() || "";
    // Without a label the trigger/header would have no accessible name, so skip
    // rendering rather than emit an unlabeled interactive control.
    if (!triggerName) {
        return null;
    }

    const mobileIndex = props.mobileContentIndex ?? 0;

    const headerCtaUrl = props.value.cta?.url || collections[0]?.cta_link?.url;
    const headerCtaLabel =
        props.value.cta?.title || linkCollectionCtaLabel(collections[0]);
    const showHeaderCta = Boolean(headerCtaUrl && headerCtaLabel);

    if (viewport.belowMobile) {
        const primaryVariant = linkCollectionVariantClass(
            collections[0]?.variant,
        );
        return (
            <section
                className={concatClassNames([styles.container, primaryVariant])}
            >
                <MobileContentPanel
                    collections={collections}
                    mobileIndex={mobileIndex}
                    mobileA11yIdPrefix={props.mobileA11yIdPrefix}
                    headerCtaUrl={headerCtaUrl}
                    headerCtaLabel={headerCtaLabel}
                    showHeaderCta={showHeaderCta}
                />
            </section>
        );
    }

    return (
        <section className={styles.container}>
            <NavigationMenu.Trigger className={styles.trigger}>
                <span className={styles.triggerLabel}>{triggerName}</span>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className={styles.content}>
                <div className={styles.columns}>
                    {collections.map((collection, columnIndex) => (
                        <div
                            key={`${collection.name}-${columnIndex}`}
                            className={classNames([
                                [styles.column, true],
                                [styles.columnFirst, columnIndex === 0],
                            ])}
                        >
                            {columnIndex === 0 ? (
                                <ColumnHeader
                                    title={collection.name}
                                    ctaUrl={headerCtaUrl}
                                    ctaLabel={headerCtaLabel}
                                />
                            ) : (
                                <h3 className={styles.columnTitle}>
                                    {collection.name}
                                </h3>
                            )}
                            <RichNavigationLinkCollectionContent
                                value={collection}
                                isCurrentCollectionOpen={true}
                                suppressCta={columnIndex === 0 && showHeaderCta}
                            />
                            {columnIndex === 0 && (
                                <ColumnFooter collection={collection} />
                            )}
                        </div>
                    ))}
                </div>
            </NavigationMenu.Content>
        </section>
    );
};
